/**
 * Single source of truth for the page-access rule grammar. Pure function with
 * no Vue / no h3 deps so it can run in three places that previously each
 * carried their own copy:
 *
 *   - `useAuth().evaluateRule()`        — client route guard / template gate
 *   - `app/middleware/auth.global.ts`   — SPA navigation gate (via useAuth)
 *   - `server/middleware/auth.ts`       — Nitro/Worker SSR gate (the
 *                                          authoritative security boundary)
 *
 * Auth rule semantics ARE the security boundary; structural single-sourcing
 * removes the "kept in lock-step" manual discipline that previously linked
 * the client and server implementations.
 *
 * Grammar:
 *
 *   undefined | false              → public, always allowed
 *   true                           → any authenticated user
 *   { claim: value }               → claims[claim] equals value
 *   { claim: [v1, v2] }            → claims[claim] matches any of
 *   { claimA: a, claimB: b }       → AND across keys
 *
 * Claim values may be arrays on the user side (e.g. `role: ["admin",
 * "editor"]`) — the matcher succeeds when the rule's expected set
 * intersects the actual set. Numeric / non-string claim values are
 * coerced to strings before comparison. Unknown rule shapes fail closed.
 */
export type AuthRule = boolean | Record<string, string | string[]>;

export function evaluateAuthRule(
  rule: AuthRule | undefined,
  claims: Record<string, unknown>,
  isAuthenticated: boolean,
): boolean {
  if (rule === undefined || rule === false) return true;
  if (!isAuthenticated) return false;
  if (rule === true) return true;
  if (typeof rule !== "object" || rule === null) return false;

  for (const [key, expected] of Object.entries(rule)) {
    const expectedArr = Array.isArray(expected) ? expected : [expected];
    const actual = claims[key];
    const actualArr = Array.isArray(actual)
      ? actual.map(String)
      : actual === undefined || actual === null
        ? []
        : [String(actual)];
    if (!expectedArr.some((e) => actualArr.includes(e))) return false;
  }
  return true;
}
